<template>
	<div class="row centered">
		<AppTable ref="transactions-table"
			v-if="userStore.currBusinessId"
			:fetch-function="administrationStore.getFilteredStripeTransactions"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="getString('transactions_history')"
			:total-items="administrationStore.businessTransactionsTotal || 0"
			:items="transactions"
			initial-sort-field="date"
			:hidden-filters="hiddenFilters"
			@failed-fetch="details=>{$emit('failed-fetch', details)}"
			:app-select-values="['10', '25', '50', '100','1000']"
			allow-download
		  	:export-popup-z-index="1021"
			@export="exportTransactions"
		>

			<template v-slot:refund="{item}">
				<div class="row centered">
					<AppTableButtonGroup :buttons="buttonsRefund" @picked-refund="popupRefundStripeTransaction(item)" :class="{disabled: !item.canRefund}"/>
				</div>
			</template>

			<template v-slot:print="{item}">
				<div class="row centered">
					<AppTableButtonGroup :buttons="buttonsReceipt" @picked-receipt="selectedReceiptItem = item;showReceiptManager = true"/>
				</div>
			</template>

			<template v-slot:payment_intent_id="{item}">
				<div class="row centered">
					<p class="payment-intent" :class="{clickable: !!item.order_id}" @click="goToOrder(item.order_id)">{{ item.payment_intent_id }}</p>
				</div>
			</template>

		</AppTable>
		<AppPopup v-if="refundingTx"
			ref="refund-popup"
			:buttons="refundingTx.popupButtons"
			:title="getString('refund_transaction')"
			:z-index="1030"
			prevent-overlay-dismiss
		>
			<div class="col-12 centered p-20 popup-refund" :class="{loading: refundingTx.loadingRefund}">
				<AppLoader v-show="refundingTx.loadingRefund" />
				<div class="row centered">
					<p v-html="getString('refund_x', [currencyStore.readableCurrencyValue(refundingTx.amount)])"></p>
					<AppButtonIcon class="ml-10" @click-icon="()=>{refundingTx.showPartialAmount = !refundingTx.showPartialAmount}" small icon-name="icon-edit-white" />
				</div>
				<div class="row">
					<AppDropdown :collapsed="!refundingTx.showPartialAmount">
						<div class="row centered space-between p-10">
							<p>{{ getString('insert_refund_amount') }}</p>
							<AppNumberInput double-decimal :symbol="currencyStore.currCurrency.symbol" :min="0" :max="currencyStore.dbToActualValue(refundingTx.amount)" :value="currencyStore.dbToActualValue(refundingTx.amount)" @changed-value="changedRefundValue" />
						</div>
					</AppDropdown>
				</div>
				<AppSelect
					class="mt-10"
					relative
					:label="getString('reason')"
					:values="[this.getString('reason_duplicate'), this.getString('reason_requested_by_customer')]"
					@selected="(index) => {refundingTx.reason = index !== null ? ['duplicate', 'requested_by_customer'][index] : null}"
				/>
			</div>
		</AppPopup>
		<ReceiptManager :active="showReceiptManager" @toggling-popup="toggleReceiptManager" @success="$refs['receipt-sent'].openNotification()" :item="selectedReceiptItem" />
		<AppNotification :title="getString('success')" :body-text="getString('email_sent')" ref="receipt-sent" :type="1" />
	</div>
</template>

<script>
import AppLoader from "@/shared/components/AppLoader.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppTable from "@/shared/components/AppTable.vue";
import {useUserStore, useAdministrationStore, useOrdersStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useScriptStore} from "@/shared/stores";
import {lessThan24HoursAgo, formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";
import ReceiptManager from "@/console-pos/components/ReceiptManager.vue";
import AppNotification from "@/shared/components/AppNotification.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";

export default {
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const languageStore = useLanguageStore();
		const ordersStore = useOrdersStore();
		let notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		return {userStore, ordersStore, currencyStore, administrationStore, getString: languageStore.getString, showNotification: notificationStore.showNotification, scriptStore}
	},
	name: "TransactionsTable",
	emits: ["failed-fetch", "export"],
	components: {
		AppTableButtonGroup,
		AppNotification, AppTable, AppPopup, AppDropdown, AppLoader, AppSelect, AppNumberInput, AppButtonIcon, ReceiptManager},
	props: {
		hiddenFilters: {
			type: Object,
			default: {},
			//object like fieldName => filterValue (es. {type: ["connect_order_pos", 'pos_order'], status: 'pending', ...})
			validator(hiddenFilters) {
				return hiddenFilters === null || Object.keys(hiddenFilters).length === 0 || Object.keys(hiddenFilters).length;
			},
		},
	},
	data() {
		return {
			showReceiptManager: false,
			selectedReceiptItem: null,
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'transactions',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
				{
					type: "checklist",
					key: "status",
					text: this.getString("status"),
					values: [this.getString("canceled"), this.getString("succeeded"), this.getString("refunded"), this.getString("partially_refunded")],
					keysArray: ['canceled', 'succeeded', 'refunded', 'partially_refunded'],
					active: false,
					selectedIndexes: [],
					finalValue: '',
				},
				{
					type: "price",
					key: "amount",
					text: this.getString("amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "text",
					key: "payment_intent_id",
					text: this.getString("id"),
					placeholder: this.getString("id"),
					value: "",
					active: false,
					finalValue: '',
				},
				{
					type: "price",
					key: "refunded_amount",
					text: this.getString("refunded_amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString("date"),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.getString("status"),
					fieldName: 'status',
					cols: 1,
				},
				{
					text: this.getString("amount"),
					cols: 1,
					sortable: true,
					fieldName: 'amount',
				},
				{
					text: this.getString("receipt"),
					cols: 1,
					fieldName: 'print_receipt',
					slotName: 'print',
				},
				{
					text: this.getString("refund"),
					cols: 1,
					fieldName: 'refund',
					slotName: 'refund',
				},
				{
					text: this.getString("refunded_amount"),
					cols: 1,
					sortable: true,
					fieldName: 'refunded_amount',
					formatter: 'currency'
				},
				{
					text: this.getString("id"),
					cols: 2,
					fieldName: 'payment_intent_id',
					slotName: 'payment_intent_id',
				},
			],
			refundingTx: null,
		}
	},
	computed: {
		buttonsRefund() {
			return [
				{
					iconName: "icon-refund-blue-new",
					emitName: "picked-refund",
					disabled: !this.userStore.checkOrderPayPosAllowed
				},
			];
		},
		buttonsReceipt() {
			return [
				{
					iconName: "icon-send-receipt-blue-new",
					emitName: "picked-receipt",
				},
			];
		},
		transactions() {
			if(!this.administrationStore.businessTransactions || !this.administrationStore.businessTransactions.length) { return []; }
			return this.administrationStore.businessTransactions.map(transaction => {
				transaction.canRefund = !transaction.refunded_amount && transaction.status === 'succeeded' && lessThan24HoursAgo(transaction.date);
				if(transaction.canRefund) {
					transaction.popupButtons = transaction.loadingRefund ? [] : [
						{text: this.getString('cancel'), callback: null, negative: true},
						{text: this.getString('ok'), callback: () => {this.refundStripeTransaction(transaction)}},
					]
				}
				return transaction;
			});
		},
	},
	methods: {
		changedRefundValue(value) {
			this.refundingTx.refundingAmount = this.currencyStore.actualToDbValue(value);
		},
		goToOrder(orderId) {
			if(orderId) {
				this.ordersStore.search = "id:"+orderId;
				this.ordersStore.showCompleted = true;
				this.ordersStore.filterCounter = true;
				this.ordersStore.filterDelivery = true;
				this.ordersStore.filterTakeaway = true;
				this.ordersStore.filterRooms = true;
				this.ordersStore.refreshOrders();
				this.$router.push("/ops/orders");
			}
		},
		toggleReceiptManager(isOpen) {
			if(!isOpen) {
				this.showReceiptManager = false;
			}
		},
		popupRefundStripeTransaction(item) {
			this.refundingTx = item;
			this.$nextTick(() => {
				this.$refs['refund-popup'].toggleStatus(true);
			})
		},
		refundStripeTransaction(item) {
			item.loadingRefund = true;
			item.showPartialAmount = false;
			let refundingAmount = item.refundingAmount || null;
			let refundingReason = item.reason || null;
			item.canRefund = false;
			this.administrationStore.refundStripeTransaction(item.transaction_id, refundingAmount, refundingReason, (success, details)=>{
				if(success) {
					this.$refs['transactions-table'].fetchData();
					this.showNotification('refunded-transaction-success');
				} else {
					this.$emit('failed-fetch', details)
				}
				this.$refs['refund-popup'].toggleStatus(false);
				item.loadingRefund = false;
			});
		},
		exportTransactions(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let today = new Date().toLocaleString();
			let fileName = businessNameNorm+"_"+this.getString('transactions_history').toLowerCase()+"_"+today;
			exportToFile({
				columns: [
					{ header: this.getString('date'), value: t => formatDateTimeWithSeconds(t.date, false) },
					{ header: this.getString('status'), value: t => this.getString(t.status) },
					{ header: this.getString('amount') + " " + sym, value: t => this.currencyStore.dbToActualValue(t.amount), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('refunded_amount') + " " + sym, value: t => this.currencyStore.dbToActualValue(t.refunded_amount), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('id'), key: 'payment_intent_id' },
				],
				rows: this.administrationStore.businessTransactions,
				fileName,
				sheetName: this.getString("transactions_history"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		},
	},
	watch: {
		'userStore.currBusinessId': {
			handler(newVal, oldVal) {
				if(newVal && newVal !== oldVal) {
					if(this.$refs['transactions-table']) {
						this.$refs['transactions-table'].fetchData();
					}
				}
			},
		},
	},
}
</script>
<style lang="scss" scoped>
 .popup-refund {
	 position: relative;
	 &.loading > *:not(.loader-container) {
		 opacity: 0;
	 }
	 &.loading .row .dropdown-container, &.loading .row .dropdown-container:deep(.dropdown) {
		 opacity: 0;
	 }
	 .loader-container {
		 position: absolute;
		 top: 50%;
		 left: 50%;
		 transform: translate(-50%, -50%);
	 }
 }

 .payment-intent {
	 text-align: left;
	 width: 100%;
	 font-size: 12px;

	 &.clickable {
		 cursor: pointer;
		 color: var(--color-primary);
		 text-decoration: underline;
	 }
 }
</style>